import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Download, Upload, FileSpreadsheet, History, RotateCcw, Check, X } from "lucide-react";
import {
  downloadListino,
  downloadTemplate,
  downloadProjectsList,
  importPreview,
  importConfirm,
  importHistory,
  importRestore,
} from "../lib/projects";

const STATUSES = [
  ["", "Tutti gli stati"],
  ["attivo", "Attivi"],
  ["salvato", "Salvati"],
  ["confermato", "Confermati"],
  ["completato", "Completati"],
  ["inattivo", "Inattivi"],
];

export const AdminDataIO = ({ token }) => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [filters, setFilters] = useState({ status: "", from_date: "", to_date: "", customer: "" });
  const fileRef = useRef(null);

  const loadHistory = async () => {
    try {
      setHistory(await importHistory(token));
    } catch {
      /* silenzioso */
    }
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      if (ok) toast.success(ok);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operazione non riuscita");
    } finally {
      setBusy(false);
    }
  };

  const pickFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      setPreview(await importPreview(token, file));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "File non leggibile");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const res = await importConfirm(token, preview.staging_id);
      toast.success(`Importazione completata: ${res.applied} articoli aggiornati o creati`);
      setPreview(null);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Importazione non riuscita");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="admin-data-io-section">
      <h2 className="font-display text-2xl font-semibold uppercase tracking-tight">
        Importa / Esporta dati
      </h2>
      <p className="mt-1 label-xs text-muted-foreground">
        Gestisci il listino con Excel, LibreOffice o CSV. Tutti i prezzi nei file sono NETTI, IVA
        esclusa. Il database resta la fonte del configuratore.
      </p>

      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        <div className="border border-border bg-card p-5">
          <h3 className="font-display text-base font-medium">Listino lavorazioni</h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {["xlsx", "ods", "csv"].map((f) => (
              <Button
                key={f}
                data-testid={`export-listino-${f}`}
                size="sm"
                disabled={busy}
                onClick={() => run(() => downloadListino(token, f), `Listino ${f.toUpperCase()} scaricato`)}
                className="rounded-none bg-[#1B2A20] text-[#F4F7F2] hover:bg-[#4E7C59]"
              >
                <Download className="mr-1 h-3.5 w-3.5" /> Scarica {f.toUpperCase()}
              </Button>
            ))}
            <Button
              data-testid="export-listino-template"
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => run(() => downloadTemplate(token), "Modello scaricato")}
              className="rounded-none"
            >
              <FileSpreadsheet className="mr-1 h-3.5 w-3.5" /> Modello importazione
            </Button>
          </div>

          <div className="mt-4 border-t border-border pt-4">
            <input
              ref={fileRef}
              data-testid="import-listino-input"
              type="file"
              accept=".xlsx,.ods,.csv"
              onChange={(e) => pickFile(e.target.files?.[0])}
              className="hidden"
            />
            <Button
              data-testid="import-listino-btn"
              size="sm"
              disabled={busy}
              onClick={() => fileRef.current?.click()}
              className="rounded-none bg-[#4E7C59] text-white hover:bg-[#1B2A20]"
            >
              <Upload className="mr-1 h-3.5 w-3.5" /> Importa listino
            </Button>
            <span className="ml-3 label-xs text-muted-foreground">XLSX, ODS o CSV · max 8 MB</span>
          </div>

          {preview && (
            <div className="mt-4 border border-[#4E7C59] p-4" data-testid="import-preview">
              <div className="label-xs text-[#4E7C59]">Anteprima importazione</div>
              <div className="mt-1 font-mono-t text-xs">{preview.filename}</div>
              <ul className="mt-2 space-y-0.5 text-sm">
                <li>{preview.summary.total} righe trovate</li>
                <li>{preview.summary.new} nuovi articoli</li>
                <li>{preview.summary.updated} articoli modificati</li>
                <li>{preview.summary.unchanged} articoli invariati</li>
                <li
                  className={preview.summary.errors ? "text-[#8A2E2E]" : ""}
                  data-testid="import-errors-count"
                >
                  {preview.summary.errors} errori
                </li>
              </ul>

              {preview.errors?.length > 0 && (
                <div className="mt-3 max-h-40 overflow-y-auto border border-[#8A2E2E]/40 bg-[#FDF2F2] p-2 text-xs">
                  {preview.errors.map((e, i) => (
                    <div key={i}>
                      Riga {e.row} — {e.column}: {e.problem}
                    </div>
                  ))}
                </div>
              )}

              {preview.preview?.length > 0 && (
                <div className="mt-3 max-h-40 overflow-y-auto border border-border text-xs">
                  {preview.preview.map((r, i) => (
                    <div key={i} className="flex justify-between gap-2 border-b border-border/60 px-2 py-1">
                      <span>
                        <span
                          className={`mr-2 label-xs ${r.action === "create" ? "text-[#4E7C59]" : "text-[#B08968]"}`}
                        >
                          {r.action === "create" ? "NUOVO" : "MODIFICA"}
                        </span>
                        <span className="font-mono-t">{r.code}</span> {r.name}
                      </span>
                      <span className="text-muted-foreground">{r.changes?.join(", ")}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-3 flex gap-2">
                <Button
                  data-testid="import-confirm"
                  size="sm"
                  disabled={busy}
                  onClick={confirm}
                  className="rounded-none bg-[#4E7C59] text-white hover:bg-[#1B2A20]"
                >
                  <Check className="mr-1 h-3.5 w-3.5" /> Conferma importazione
                </Button>
                <Button
                  data-testid="import-cancel"
                  size="sm"
                  variant="outline"
                  onClick={() => setPreview(null)}
                  className="rounded-none"
                >
                  <X className="mr-1 h-3.5 w-3.5" /> Annulla
                </Button>
              </div>
              <p className="mt-2 label-xs text-muted-foreground">
                Il database viene modificato solo dopo la conferma. Prima dell'importazione viene
                creata automaticamente una copia del listino.
              </p>
            </div>
          )}
        </div>

        <div className="border border-border bg-card p-5">
          <h3 className="font-display text-base font-medium">Progetti / Preventivi</h3>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="label-xs text-muted-foreground">Stato</span>
              <select
                data-testid="export-filter-status"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="mt-1 block w-full border border-border bg-background px-2 py-1.5 text-sm outline-none focus:border-[#4E7C59]"
              >
                {STATUSES.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="label-xs text-muted-foreground">Cliente</span>
              <Input
                data-testid="export-filter-customer"
                value={filters.customer}
                onChange={(e) => setFilters({ ...filters, customer: e.target.value })}
                placeholder="Nome cliente"
                className="mt-1 rounded-none text-sm"
              />
            </label>
            <label className="block">
              <span className="label-xs text-muted-foreground">Dal</span>
              <Input
                data-testid="export-filter-from"
                type="date"
                value={filters.from_date}
                onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
                className="mt-1 rounded-none text-sm"
              />
            </label>
            <label className="block">
              <span className="label-xs text-muted-foreground">Al</span>
              <Input
                data-testid="export-filter-to"
                type="date"
                value={filters.to_date}
                onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
                className="mt-1 rounded-none text-sm"
              />
            </label>
          </div>
          <Button
            data-testid="export-projects-list"
            size="sm"
            disabled={busy}
            onClick={() =>
              run(
                () => downloadProjectsList(token, { ...filters, status: filters.status || null }),
                "Elenco progetti scaricato"
              )
            }
            className="mt-3 rounded-none bg-[#1B2A20] text-[#F4F7F2] hover:bg-[#4E7C59]"
          >
            <Download className="mr-1 h-3.5 w-3.5" /> Esporta elenco progetti
          </Button>
          <p className="mt-2 label-xs text-muted-foreground">
            I file di ogni singolo progetto, preventivo e ordine confermato si scaricano aprendo il
            progetto nella sezione Progetti.
          </p>
        </div>
      </div>

      <div className="mt-4 border border-border bg-card" data-testid="import-history">
        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          <History className="h-4 w-4 text-[#4E7C59]" />
          <span className="font-display text-base font-medium">Storico importazioni</span>
        </div>
        {history.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">Nessuna importazione effettuata.</p>
        ) : (
          history.map((h) => (
            <div
              key={h.id}
              className="grid gap-2 border-b border-border px-4 py-3 text-sm last:border-0 sm:grid-cols-[160px_1fr_180px_150px]"
            >
              <span className="font-mono-t text-xs">
                {new Date(h.created_at).toLocaleString("it-IT")}
              </span>
              <span>{h.filename}</span>
              <span className="label-xs text-muted-foreground">
                {h.admin} · +{h.added} nuovi · {h.updated} modificati
              </span>
              <span className="text-right">
                <Button
                  data-testid={`restore-backup-${h.id}`}
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => {
                    if (
                      !window.confirm(
                        "Ripristinare il listino allo stato precedente a questa importazione? Le modifiche successive andranno perse."
                      )
                    )
                      return;
                    run(() => importRestore(token, h.id), "Listino precedente ripristinato").then(
                      loadHistory
                    );
                  }}
                  className="rounded-none"
                >
                  <RotateCcw className="mr-1 h-3.5 w-3.5" /> Ripristina
                </Button>
              </span>
            </div>
          ))
        )}
      </div>
    </section>
  );
};
